// frontend/src/components/masters/VesselVoyageForm.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import './VesselVoyageForm.css';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_BASE_URL = '/api/masters/vesselvoyages/'; // Django API endpoint for Vessel Voyages (hyphenated)
const SHIPPING_LINE_API_URL = '/api/masters/shippinglines/'; // API for fetching Shipping Lines (hyphenated)
const UNIQUE_VESSEL_NAMES_API_URL = '/api/masters/vesselvoyages/unique_vessels/'; // API for unique vessel names (hyphenated)

// Helper to get auth token
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    // For FormData, do not set Content-Type, let the browser do it.
    return headers;
};

const VesselVoyageForm = ({ mode, initialData, onClose, vesselReadOnly = false, isDuplicateEta, editingScheduleId }) => {
    // Form state, initialized to match models.py structure
    const [vesselVoyage, setVesselVoyage] = useState({
        vessel: '',
        eta: '',
        update_arrival_date: false,
        ata: '',
        etb: '',
        update_berthing_date: false,
        atb: '',
        port_operator: '',
        port_operator_not_known: false,
        tra_manifest_ready: '',
        voyage_details: [],
        ...initialData,
    });

    const [readOnly, setReadOnly] = useState(mode === 'view');
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [uniqueVessels, setUniqueVessels] = useState([]);
    const [shippingLines, setShippingLines] = useState([]);
    const { hasPermission } = usePermissions(); // 2. Get hook

    // Refs for file preview modal (kept for consistency, but no file inputs in this specific form)
    const previewModalRef = useRef(null);
    const previewIframeRef = useRef(null);
    const previewImageRef = useRef(null);
    const previewDownloadDivRef = useRef(null);
    const [isMaximized, setIsMaximized] = useState(false);


    // --- Data Fetching Callbacks ---


    // Fetches unique vessel names from the backend for the datalist
    const fetchUniqueVessels = useCallback(async () => {
        try {
            const response = await fetch(UNIQUE_VESSEL_NAMES_API_URL, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch unique vessels');
            const data = await response.json();
            setUniqueVessels(data.unique_vessels || []); // Expecting { unique_vessels: ["Vessel A", "Vessel B"] }
        } catch (err) {
            console.error("Error fetching unique vessels:", err);
            setError('Failed to load vessel suggestions.');
        }
    }, []);

    // Fetches all shipping lines for the dropdown in Voyage Details
    const fetchShippingLines = useCallback(async () => {
        try {
            const response = await fetch(SHIPPING_LINE_API_URL, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch shipping lines');
            const data = await response.json();
            // Assuming data is an array of ShippingLine objects with 'id' and 'name'
            setShippingLines(data);
        } catch (err) {
            console.error("Error fetching shipping lines:", err);
            setError('Failed to load shipping lines.');
        }
    }, []);

    // --- Effects ---

    // Effect to load initial data and set readOnly mode
    useEffect(() => {
        if (initialData) {
            // Transform initialData to match form state for dates and booleans
            const transformedData = {
                ...initialData,
                eta: initialData.eta ? initialData.eta.split('T')[0] : '', // Extract date part
                etb: initialData.etb ? initialData.etb.split('T')[0] : '', // Extract date part
                ata: initialData.ata ? initialData.ata.split('T')[0] : '', // Extract date part
                atb: initialData.atb ? initialData.atb.split('T')[0] : '', // Extract date part
                tra_manifest_ready: initialData.tra_manifest_ready ? initialData.tra_manifest_ready.slice(0, 16) : '', // Format for datetime-local
                port_operator_not_known: initialData.port_operator_not_known || false, 
                // Ensure voyage_details are correctly structured.
                // When coming from backend, shipping_line will be a nested object,
                // for the form's select, we need its ID.
                voyage_details: initialData.voyage_details ? initialData.voyage_details.map(detail => ({
                    ...detail,
                    shipping_line: detail.shipping_line?.id || '', // Use shipping_line.id for form
                })) : [],
            };
            setVesselVoyage(transformedData);
        } else {
            // Set default empty state for new forms
            setVesselVoyage({
                vessel: '',
                eta: '',
                update_arrival_date: false,
                etb: '',
                update_berthing_date: false,
                ata: '',
                atb: '',
                port_operator: '',
                port_operator_not_known: false,
                tra_manifest_ready: '',
                voyage_details: [],
            });
        }
        setReadOnly(mode === 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Fetch data on component mount
    useEffect(() => {
        fetchUniqueVessels();
        fetchShippingLines();
    }, [fetchUniqueVessels, fetchShippingLines]);

    // DOM-level uppercase enforcement
    const enforceUppercaseInputs = useCallback(() => {
        document.querySelectorAll('.vessel-voyage-form-container input[type="text"], .vessel-voyage-form-container textarea').forEach(input => {
            if (!input.dataset.uppercaseListener) {
                input.addEventListener('input', function () {
                    this.value = this.value.toUpperCase();
                });
                input.dataset.uppercaseListener = "true";
            }
        });
    }, []);

    useEffect(() => {
        enforceUppercaseInputs();
    }, [enforceUppercaseInputs]);


    // --- Form Field Change Handlers ---

    // Handles changes for top-level input fields
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (readOnly) return;

        let newValue = value;
        // Handle boolean checkboxes
        if (type === 'checkbox') {
            newValue = checked;
        } else if (type === 'text' || type === 'textarea') {
            // Apply uppercase only for text/textarea inputs
            newValue = value.toUpperCase();
        }
        

         // Update the state
        const updatedVesselVoyage = { ...vesselVoyage, [name]: newValue };
        // Handle mutual exclusivity between port_operator and port_operator_not_known
        if (name === 'port_operator' && value) {
            // If port operator is selected, uncheck "Not Known"
            updatedVesselVoyage.port_operator_not_known = false;
        } else if (name === 'port_operator_not_known' && checked) {
            // If "Not Known" is checked, clear port operator selection
            updatedVesselVoyage.port_operator = '';
        }

        if (name === 'update_arrival_date' && checked) {
            updatedVesselVoyage.ata = updatedVesselVoyage.eta || '';
        }
        if (name === 'update_berthing_date' && checked) {
            updatedVesselVoyage.atb = updatedVesselVoyage.etb || '';
        }
        setVesselVoyage(updatedVesselVoyage);

        // Real-time validation for ETB vs ETA
        if ((name === 'eta' || name === 'etb') && updatedVesselVoyage.eta && updatedVesselVoyage.etb) {
            const etaDate = new Date(updatedVesselVoyage.eta);
            const etbDate = new Date(updatedVesselVoyage.etb);
            
            if (etbDate < etaDate) {
                setError('ETB date cannot be before ETA date.');
            } else {
                // Clear error if dates are valid
                if (error && error.includes('ETB date cannot be before ETA date')) {
                    setError(null);
                }
            }
        }
    };

    // Handles changes for nested fields within voyage_details
    const handleVoyageDetailChange = (index, fieldName, value) => {
        if (readOnly) return;
        setVesselVoyage(prev => {
            const updatedDetails = [...prev.voyage_details];
            let newValue = value;
            if (['voyage_number'].includes(fieldName)) {
                newValue = value.toUpperCase();
            }
            updatedDetails[index] = { ...updatedDetails[index], [fieldName]: newValue };
            return { ...prev, voyage_details: updatedDetails };
        });
    };

    const addVoyageDetail = () => {
        if (readOnly) return;
        setVesselVoyage(prev => ({
            ...prev,
            // Initialize shipping_line to an empty string, representing no selection
            voyage_details: [...prev.voyage_details, { voyage_number: '', shipping_line: '' }]
        }));
    };

    const removeVoyageDetail = (index) => {
        if (readOnly) return;
        setVesselVoyage(prev => ({
            ...prev,
            voyage_details: prev.voyage_details.filter((_, i) => i !== index)
        }));
    };

    const isETBBeforeETA = () => {
        if (vesselVoyage.eta && vesselVoyage.etb) {
            const etaDate = new Date(vesselVoyage.eta);
            const etbDate = new Date(vesselVoyage.etb);
            return etbDate < etaDate;
        }
        return false;
    };

    const isATBBeforeATA = () => {
        if (vesselVoyage.ata && vesselVoyage.atb) {
            const ataDate = new Date(vesselVoyage.ata);
            const atbDate = new Date(vesselVoyage.atb);
            return atbDate < ataDate;
        }
        return false;
    };

    const toggleMaximize = () => {
        if (!previewModalRef.current) return;
        const modal = previewModalRef.current;
        const btn = modal.querySelector('#maximizeBtn');

        if (!isMaximized) {
            modal.dataset.originalLeft = modal.style.left;
            modal.dataset.originalTop = modal.style.top;
            modal.dataset.originalWidth = modal.style.width;
            modal.dataset.originalHeight = modal.style.height;

            modal.style.top = '0';
            modal.style.left = '0';
            modal.style.width = '100vw';
            modal.style.height = '100vh';
            modal.style.resize = 'none';
            btn.textContent = 'Restore';
        } else {
            modal.style.top = modal.dataset.originalTop;
            modal.style.left = modal.dataset.originalLeft;
            modal.style.width = modal.dataset.originalWidth;
            modal.style.height = modal.dataset.originalHeight;
            modal.style.resize = 'both';
            btn.textContent = 'Maximize';
        }
        setIsMaximized(!isMaximized);
    };

    // Make modal draggable
    useEffect(() => {
        const modal = previewModalRef.current;
        if (!modal) return;
        const header = modal.querySelector('#modalHeader');
        let isDragging = false;
        let offsetX = 0, offsetY = 0;

        const onMouseDown = (e) => {
            isDragging = true;
            offsetX = e.clientX - modal.offsetLeft;
            offsetY = e.clientY - modal.offsetTop;
            document.body.style.userSelect = 'none';
        };
        const onMouseUp = () => {
            isDragging = false;
            document.body.style.userSelect = 'auto';
        };
        const onMouseMove = (e) => {
            if (isDragging) {
                modal.style.left = (e.clientX - offsetX) + 'px';
                modal.style.top = (e.clientY - offsetY) + 'px';
            }
        };

        if (header) {
            header.addEventListener('mousedown', onMouseDown);
            document.addEventListener('mouseup', onMouseUp);
            document.addEventListener('mousemove', onMouseMove);
        }

        return () => {
            if (header) {
                header.removeEventListener('mousedown', onMouseDown);
                document.removeEventListener('mouseup', onMouseUp);
                document.removeEventListener('mousemove', onMouseMove);
            }
        };
    }, []);

    const validateForm = () => {
        const errors = [];
        
        // Validate ETB is not before ETA
        if (vesselVoyage.eta && vesselVoyage.etb && vesselVoyage.etb.trim() !== '') {
            const etaDate = new Date(vesselVoyage.eta);
            const etbDate = new Date(vesselVoyage.etb);
            
            if (etbDate < etaDate) {
                errors.push('ETB date cannot be before ETA date.');
            }
        }
        
        // Validate Port Operator requirement
        if (!vesselVoyage.port_operator && !vesselVoyage.port_operator_not_known) {
            errors.push('Port Operator must be selected or marked as "Not Known".');
        }
        
        return errors;
    };

    // --- Form Submission Logic ---

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return;

        setError(null);
        setMessage(null);

        // Duplicate ETA check (add/edit)
        if (isDuplicateEta && vesselVoyage.vessel && vesselVoyage.eta) {
            const excludeId = initialData?.id || editingScheduleId || null;
            if (isDuplicateEta(vesselVoyage.vessel, vesselVoyage.eta, excludeId)) {
                toast.warn('A schedule for this vessel with the same ETA already exists.');
                setError('A schedule for this vessel with the same ETA already exists.');
                return;
            }
        }

        // Validate form before submission
        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            setError(validationErrors.join(' '));
            return;
        }

        const formData = new FormData();

        // Append top-level fields
        // Ensure date/datetime formats match Django's expectations if they aren't auto-converted by type
        formData.append('vessel', vesselVoyage.vessel.toUpperCase());
        formData.append('eta', vesselVoyage.eta);
        formData.append('update_arrival_date', vesselVoyage.update_arrival_date ? 'true' : 'false');
         // Handle ETB - only append if it has a value, otherwise don't send it
        if (vesselVoyage.etb && vesselVoyage.etb.trim() !== '') {
        formData.append('etb', vesselVoyage.etb);
        } else {
            // Explicitly send null for empty ETB
            formData.append('etb', '');
        }
        formData.append('update_berthing_date', vesselVoyage.update_berthing_date ? 'true' : 'false');
        formData.append('ata', vesselVoyage.ata);
        formData.append('atb', vesselVoyage.atb);
        formData.append('port_operator', vesselVoyage.port_operator.toUpperCase());
        formData.append('port_operator_not_known', vesselVoyage.port_operator_not_known ? 'true' : 'false'); // New field
        // Handle TRA Manifest Ready which is datetime-local, sending empty string if not set
        formData.append('tra_manifest_ready', vesselVoyage.tra_manifest_ready || '');

        // Append voyage_details as a JSON string
        // Map shipping_line object to its ID for backend
        const serializedVoyageDetails = vesselVoyage.voyage_details.map(detail => ({
            voyage_number: detail.voyage_number.toUpperCase(),
            shipping_line: detail.shipping_line // This should already be the ID from the select
        }));
        formData.append('voyage_details', JSON.stringify(serializedVoyageDetails));

        // Log FormData contents for debugging
        console.log("--- FormData contents before sending (VesselVoyageForm) ---");
        for (let pair of formData.entries()) {
            console.log(pair[0]+ ': ' + pair[1]);
        }
        console.log("-----------------------------------------------------");

        const url = initialData?.id ? `${API_BASE_URL}${initialData.id}/` : API_BASE_URL;
        const method = initialData?.id ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: getAuthHeaders(true),
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = Object.entries(errorData).map(([key, val]) => `${key}: ${val}`).join('; ');
                setError(`Save failed: ${errorMessage}`);
                toast.error(`Save failed: ${errorMessage}`);
                return;
            }

            toast.success('Vewssel Voyage saved successfully!');
            if (onClose) onClose(true);

        } catch (err) {
            setError('Failed to save Vessel Voyage. Network error or server unreachable.');
            toast.error('Failed to save Vessel Voyage. Network error or server unreachable.');
        }
    };

    const handleCancel = () => {
        onClose(false); // Notify parent to close without refreshing list
    };

    // Log the current mode and readOnly state directly before render for debugging
    console.log(`[RENDER] Current mode: "${mode}", readOnly state: "${readOnly}"`);
    console.log(`[RENDER] Vessel Voyage state:`, vesselVoyage);

    // Port Operator Choices
    const PORT_OPERATOR_CHOICES = ["TEAGTL", "DP WORLD"];

    return (
        <div className="vessel-voyage-form-container">
            <h1>Vessel Voyage Details</h1>
            <div className="toggle-buttons">
                <button type="button" onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'block')}>Expand All</button>
                <button type="button" onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'none')}>Collapse All</button>
            </div>

            {error && <div className="form-error">{error}</div>}
            {message && <div className="form-success">{message}</div>}

            <form onSubmit={handleSubmit}>
                {/* Vessel Voyage Information Section */}
                <div className="section">
                    <h2 onClick={(e) => {
                        const content = e.currentTarget.nextElementSibling;
                        if (content) content.style.display = content.style.display === 'block' ? 'none' : 'block';
                    }}>Vessel Voyage Information</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="row">
                            <div style={{ maxWidth: '250px' }}>
                                <label className="required">Vessel</label>
                                <input
                                    list="vesselDatalist"
                                    id="vessel"
                                    name="vessel"
                                    value={vesselVoyage.vessel}
                                    onChange={handleChange}
                                    required
                                    readOnly={readOnly || vesselReadOnly}
                                    style={vesselReadOnly ? { background: '#f4f4f4', cursor: 'not-allowed' } : undefined}
                                />
                                <datalist id="vesselDatalist">
                                    {uniqueVessels.map((vesselName, index) => (
                                        <option key={index} value={vesselName} />
                                    ))}
                                </datalist>
                            </div>
                        </div>
                        <div className="row date-checkbox-row"> {/* Apply date-checkbox-row class */}
                            <div className="date-checkbox-group">
                                <div className="date-field">
                                    <label htmlFor="eta">ETA</label>
                                    <input type="date" id="eta" name="eta" value={vesselVoyage.eta} onChange={handleChange} required readOnly={readOnly} />
                                </div>
                                <div className="checkbox-field">
                                    <input type="checkbox" id="update_arrival_date" name="update_arrival_date" checked={vesselVoyage.update_arrival_date} onChange={handleChange} disabled={readOnly} />
                                    <label htmlFor="update_arrival_date">Update Arrival Date</label>
                                </div>
                                <div className="date-field">
                                    <label htmlFor="ata">ATA</label>
                                    <input type="date" id="ata" name="ata" value={vesselVoyage.ata} onChange={handleChange} readOnly={readOnly} />
                                </div>
                            </div>
                            <div className="date-checkbox-group">
                                <div className="date-field">
                                    <label htmlFor="etb">ETB</label>
                                    <input 
                                        type="date" 
                                        id="etb" 
                                        name="etb" 
                                        value={vesselVoyage.etb} 
                                        onChange={handleChange} 
                                        readOnly={readOnly}
                                        className={isETBBeforeETA() ? 'error' : ''}
                                    />
                                    {isETBBeforeETA() && (
                                        <span className="validation-error">ETB cannot be before ETA</span>
                                    )}
                                </div>
                                <div className="checkbox-field">
                                    <input type="checkbox" id="update_berthing_date" name="update_berthing_date" checked={vesselVoyage.update_berthing_date} onChange={handleChange} disabled={readOnly} />
                                    <label htmlFor="update_berthing_date">Update Berthing Date</label>
                                </div>
                                <div className="date-field">
                                    <label htmlFor="atb">ATB</label>
                                    <input 
                                        type="date" 
                                        id="atb" 
                                        name="atb" 
                                        value={vesselVoyage.atb} 
                                        onChange={handleChange} 
                                        readOnly={readOnly}
                                        className={isATBBeforeATA() ? 'error' : ''}
                                    />
                                    {isATBBeforeATA() && (
                                        <span className="validation-error">ATB cannot be before ATA</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="row operator-manifest-row">
                            <div className="operator-field">
                                <div className="operator-container">
                                    <div className="operator-select-group">
                                        <label className={!vesselVoyage.port_operator_not_known ? "required" : ""}>Port Operator</label>
                                        <select 
                                            name="port_operator" 
                                            value={vesselVoyage.port_operator} 
                                            onChange={handleChange} 
                                            required={!vesselVoyage.port_operator_not_known}
                                            disabled={readOnly || vesselVoyage.port_operator_not_known}
                                        >
                                            <option value="">Select Operator</option>
                                            {PORT_OPERATOR_CHOICES.map(operator => (
                                                <option key={operator} value={operator}>{operator}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="checkbox-field">
                                        <input 
                                            type="checkbox" 
                                            id="port_operator_not_known" 
                                            name="port_operator_not_known" 
                                            checked={vesselVoyage.port_operator_not_known} 
                                            onChange={handleChange} 
                                            disabled={readOnly} 
                                        />
                                        <label htmlFor="port_operator_not_known">Not Known</label>
                                    </div>
                                </div>
                            </div>
                            <div className="manifest-field">
                                <label htmlFor="tra_manifest_ready">TRA Manifest Ready</label>
                                <input 
                                    type="datetime-local" 
                                    id="tra_manifest_ready" 
                                    name="tra_manifest_ready" 
                                    value={vesselVoyage.tra_manifest_ready} 
                                    onChange={handleChange} 
                                    readOnly={readOnly} 
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Voyage Details Section (Repeatable) */}
                <div className="section">
                    <h2 onClick={(e) => {
                        const content = e.currentTarget.nextElementSibling;
                        if (content) content.style.display = content.style.display === 'block' ? 'none' : 'block';
                    }}>Voyage Details</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="repeatable" id="voyageDetails">
                            {vesselVoyage.voyage_details.map((detail, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '1', minWidth: '140px' }}>
                                        <label className="required">Voyage</label>
                                        <input type="text" value={detail.voyage_number} onChange={(e) => handleVoyageDetailChange(index, 'voyage_number', e.target.value)} required readOnly={readOnly} />
                                    </div>
                                    <div style={{ flex: '1', minWidth: '140px' }}>
                                        <label className="required">Shipping Line</label>
                                        <select
                                            value={detail.shipping_line}
                                            onChange={(e) => handleVoyageDetailChange(index, 'shipping_line', e.target.value)}
                                            required
                                            disabled={readOnly}
                                        >
                                            <option value="">Select Shipping Line</option>
                                            {shippingLines.map(line => (
                                                <option key={line.id} value={line.id}>{line.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {!readOnly && (
                                        <button type="button" onClick={() => removeVoyageDetail(index)} className="remove-btn">Remove</button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {!readOnly && (
                            <button type="button" onClick={addVoyageDetail} className="add-btn">Add Voyage Details</button>
                        )}
                    </div>
                </div>

                <div className="buttons">
                    {/* 3. Protect Edit button */}
                    {readOnly && hasPermission('VESSELVOYAGES', 'can_edit') && (
                        <button type="button" onClick={() => setReadOnly(false)}>Edit</button>
                    )}
                    {/* 4. Protect Save button */}
                    {!readOnly && (
                        <>
                            <button type="submit">Save</button>
                            <button type="button" onClick={handleCancel} className="cancel-btn">Cancel</button>
                        </>
                    )}
                </div>
            </form>
        </div>
    );
};

export default VesselVoyageForm;
